import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  Users,
  ChevronDown,
  ChevronUp,
  Phone,
  Globe,
  FileText,
  Building2,
  Shield,
  Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { useAuthStore } from '@/features/auth';
import toast from 'react-hot-toast';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { localizedName } from '@/utils/localized';
import type { User } from '@/features/auth';
import {
  PageHeader,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  InfiniteScrollSentinel,
  UserAvatar,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useActiveRoles, useRoleDetail, useAvailablePermissions } from '../hooks';
import type { ActiveRole } from '../types/roles';

const PAGE_SIZE = 20;

interface UserListResponse {
  success: boolean;
  users: (User & { _count?: { reports: number; userHospitals: number } })[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// --- Detail Panel (rendered inside expanded card) ---
function UserDetailPanel({
  user,
  roleMap,
}: {
  user: User & { _count?: { reports: number; userHospitals: number } };
  roleMap: Map<string, ActiveRole>;
}) {
  const { t, i18n } = useTranslation();
  const matchedRole = roleMap.get(user.role);
  const { data: roleDetail, isLoading: roleLoading } = useRoleDetail(matchedRole?.id);
  const { data: availablePermissions } = useAvailablePermissions();

  const tabPermissions = availablePermissions?.tab ?? [];
  const actionPermissions = availablePermissions?.action ?? [];
  const dataScopePermissions = availablePermissions?.dataScope ?? [];

  const permissionSet = useMemo(
    () => new Set(roleDetail?.permissions?.map((p) => p.permission) ?? []),
    [roleDetail],
  );
  const isSuperAdminRole = matchedRole?.isSystem ?? false;

  const infoCardStyle = {
    backgroundColor: ADMIN_COLORS.surface,
    border: `1px solid ${ADMIN_COLORS.border}`,
  };

  const renderPermissionSection = (title: string, permissions: readonly string[]) => (
    <div className="space-y-1.5">
      <h4 className="text-xs font-bold opacity-70">{title}</h4>
      <div className="flex flex-wrap gap-1">
        {permissions.map((perm) => {
          const has = isSuperAdminRole || permissionSet.has(perm);
          return (
            <span
              key={perm}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{
                backgroundColor: has ? 'rgba(10, 149, 72, 0.12)' : 'rgba(148, 163, 184, 0.15)',
                color: has ? '#076B34' : '#64748b',
                border: `1px solid ${has ? 'rgba(4, 120, 87, 0.25)' : 'rgba(148, 163, 184, 0.25)'}`,
              }}
            >
              {t(`rolesAdmin.permissionLabels.${perm}`, perm)}
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="pt-3 space-y-3">
      {/* Info cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {/* Role */}
        <div className="rounded-xl p-2.5 text-center" style={infoCardStyle}>
          <Shield className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <p className="text-xs font-bold truncate flex items-center justify-center gap-1.5">
            {matchedRole?.color && (
              <span
                className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                style={{ backgroundColor: matchedRole.color }}
              />
            )}
            {matchedRole ? localizedName(matchedRole, i18n.language) : user.role}
          </p>
          <p className="text-[10px] opacity-60 font-semibold">{t('users.role')}</p>
        </div>

        {/* Phone */}
        <div className="rounded-xl p-2.5 text-center" style={infoCardStyle}>
          <Phone className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <p className="text-xs font-bold truncate">
            <span dir="ltr">{user.phone || t('admin.impersonation.noPhone')}</span>
          </p>
          <p className="text-[10px] opacity-60 font-semibold">{t('users.phone')}</p>
        </div>

        {/* Language */}
        <div className="rounded-xl p-2.5 text-center" style={infoCardStyle}>
          <Globe className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <p className="text-xs font-bold">
            {user.preferredLanguage === 'he' ? 'עברית' : 'English'}
          </p>
          <p className="text-[10px] opacity-60 font-semibold">{t('users.language')}</p>
        </div>

        {/* Reports count */}
        <div className="rounded-xl p-2.5 text-center" style={infoCardStyle}>
          <FileText className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <p className="text-sm font-bold">{user._count?.reports ?? 0}</p>
          <p className="text-[10px] opacity-60 font-semibold">{t('admin.reportsCol')}</p>
        </div>

        {/* Hospitals count */}
        <div className="rounded-xl p-2.5 text-center" style={infoCardStyle}>
          <Building2 className="w-4 h-4 mx-auto mb-1 opacity-60" />
          <p className="text-sm font-bold">{user._count?.userHospitals ?? 0}</p>
          <p className="text-[10px] opacity-60 font-semibold">{t('admin.hospitals')}</p>
        </div>
      </div>

      {/* Permissions section */}
      <div
        className="rounded-xl p-3 space-y-2.5"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
        }}
      >
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 opacity-60" />
          <span className="text-xs font-bold">{t('admin.impersonation.permissions')}</span>
        </div>

        {roleLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin opacity-50" />
          </div>
        ) : (
          <>
            {renderPermissionSection(t('rolesAdmin.tabAccess'), tabPermissions)}
            {renderPermissionSection(t('rolesAdmin.actions'), actionPermissions)}
            {renderPermissionSection(t('rolesAdmin.dataScope'), dataScopePermissions)}
          </>
        )}
      </div>
    </div>
  );
}

// --- Main Page ---
export function UserImpersonationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);

  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const { data: activeRoles } = useActiveRoles();

  const roleMap = useMemo(() => {
    const map = new Map<string, ActiveRole>();
    if (activeRoles) {
      for (const r of activeRoles) map.set(r.code, r);
    }
    return map;
  }, [activeRoles]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['impersonation-users', search],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
        status: 'ACTIVE',
      };
      if (search) params.search = search;

      const { data } = await http.get<UserListResponse>('/users', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.users.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allUsers = useMemo(
    () => data?.pages.flatMap((page) => page.users) || [],
    [data]
  );

  const impersonateMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await http.post(`/users/${userId}/impersonate`);
      return data;
    },
    onSuccess: (data) => {
      // Remove cached admin profile so it doesn't overwrite impersonated user
      queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
      startImpersonation(data.user, data.accessToken, data.permissions);
      navigate('/');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || t('common.error');
      toast.error(message);
    },
  });

  const handleViewAs = (user: User) => {
    triggerHapticFeedback();
    impersonateMutation.mutate(user.id);
  };

  const toggleExpand = (userId: string) => {
    triggerHapticFeedback();
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const isSelf = (userId: string) => currentUser?.id === userId;
  const isSystemUser = (user: User) => !!user.isSystem;

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <PageHeader
        icon={<Eye className="w-7 h-7" />}
        title={t('admin.impersonation.title')}
      />

      {/* Search */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters')}
          </h2>
        </div>
        <div className="px-6 py-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('admin.impersonation.search')}
          />
        </div>
      </motion.div>

      {/* User List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('common.error')} />
        ) : allUsers.length === 0 ? (
          <EmptyState
            icon={<Users className="w-14 h-14 md:w-16 md:h-16" />}
            message={t('usersAdmin.noUsers')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allUsers.map((user) => {
              const disabled = isSelf(user.id) || isSystemUser(user) || impersonateMutation.isPending;
              const tooltip = isSelf(user.id)
                ? t('admin.impersonation.cannotSelf')
                : isSystemUser(user)
                ? t('admin.impersonation.cannotSystem')
                : undefined;
              const isExpanded = expandedUserId === user.id;

              return (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-white/30 glass-surface overflow-hidden"
                >
                  {/* Row header — clickable to expand */}
                  <div
                    className="flex items-center justify-between w-full py-2 ps-6 pe-4 md:py-3 md:ps-6 md:pe-4 cursor-pointer"
                    onClick={() => toggleExpand(user.id)}
                  >
                    {/* User info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <UserAvatar
                        src={user.avatarUrl}
                        name={`${user.firstName} ${user.lastName}`}
                        size="md"
                        className={`md:h-12 md:w-12 border border-[${ADMIN_COLORS.border}]`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-neutral-800 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>

                    {/* Role badge + chevron + View As button */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {user.employmentRole && (
                        <span className="hidden md:inline text-xs font-medium text-slate-500 px-2 py-0.5 rounded-full border border-white/30 bg-white/30">
                          {localizedName(user.employmentRole, i18n.language)}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                      <motion.button
                        whileTap={disabled ? undefined : { scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!disabled) handleViewAs(user);
                        }}
                        disabled={disabled}
                        title={tooltip}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-white/30"
                        style={{
                          backgroundColor: disabled ? ADMIN_COLORS.surface : '#3b82f6',
                          color: disabled ? ADMIN_COLORS.navy : '#fff',
                        }}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {t('admin.impersonation.viewAs')}
                      </motion.button>
                    </div>
                  </div>

                  {/* Expandable detail panel */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div
                          className="px-6 pb-4"
                          style={{ borderTop: `1px solid ${ADMIN_COLORS.border}` }}
                        >
                          <UserDetailPanel user={user} roleMap={roleMap} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Infinite scroll sentinel */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />
    </motion.div>
  );
}
